// TooltipContainer.tsx
import { cloneElement, isValidElement, useCallback, useEffect, useState } from 'react';

import { mergeSlotProps } from '@mui/material/utils';

import { Tooltip } from '../Tooltip';
import { INITIAL_TOOLTIP_CONTAINER_TIMEOUT } from './types';

import type { ReactElement } from 'react';
import type { TooltipContainerProps } from './types';

/**
 * 툴팁 기능을 제공하는 컨테이너 컴포넌트
 * @description
 * - children으로 전달된 컴포넌트에 자동으로 onClick 및 툴팁 트리거 기능을 주입합니다.
 * - click prop이 true일 때 클릭 시 툴팁이 표시됩니다.
 * @example
 * // <TooltipContainer message="저장되었습니다" click>
 * //   <Button>저장</Button>
 * // </TooltipContainer>
 */
const TooltipContainer = (props: TooltipContainerProps) => {
  const {
    message = '',
    children,
    placement = 'top',
    click = false,
    timeout = INITIAL_TOOLTIP_CONTAINER_TIMEOUT,
    tooltipProps: tooltipPropsProp,
    disabled,
    display = 'inline-block',
    width = 'fit-content',
    ...others
  } = props;
  const childrenElement = children as ReactElement<{
    disabled?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  }>;

  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => setOpen(false), timeout);
      return () => clearTimeout(timer);
    }
  }, [open, timeout]);

  const handleClickEvent = useCallback(
    (originalOnClick?: (e: React.MouseEvent<HTMLElement>) => void) =>
      (e: React.MouseEvent<HTMLElement>) => {
        if (click) setOpen(true);
        return originalOnClick?.(e);
      },
    [click],
  );

  const tooltipProps = {
    ...(click ? { open } : {}),
    ...tooltipPropsProp,
    message,
    placement,
    slotProps: {
      ...tooltipPropsProp?.slotProps,
      popper: mergeSlotProps(tooltipPropsProp?.slotProps?.popper, { disablePortal: false }),
    },
  };

  // children이 React 엘리먼트가 아닌 경우 처리
  if (!isValidElement(childrenElement)) {
    return <>{childrenElement}</>;
  }

  const enhancedChildren = cloneElement(childrenElement, {
    ...others,
    onClick: handleClickEvent(childrenElement.props.onClick),
    disabled: disabled ?? childrenElement.props.disabled,
  });

  return (
    <Tooltip {...tooltipProps}>
      <span
        style={{ display, width }}
        onMouseEnter={() => {
          if (click && disabled) {
            setOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (click && disabled) {
            setOpen(false);
          }
        }}
      >
        {enhancedChildren}
      </span>
    </Tooltip>
  );
};

export default TooltipContainer;
